import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DESTINATIONS, type DestinationId } from "@/lib/destinations";

const SLOT =
  "flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[8px]";
const SLOT_CURRENT = `${SLOT} font-semibold text-brand-fg`;
const SLOT_IDLE = `${SLOT} font-medium text-muted`;

/**
 * The five destinations as a bar fixed to the bottom of a phone, below `lg`:
 * five equal slots, each an icon over its short label, padded past the
 * safe-area inset. The active destination's slot is the current page, and
 * with no active destination no slot is. The frame reserves the bar's height
 * under the page.
 */
export function BottomBar({ active }: { active: DestinationId | null }) {
  const { t } = useTranslation("customer");
  return (
    <nav
      aria-label={t("shell.primaryNav")}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden print:hidden"
    >
      <ul className="flex h-bottom-bar px-1.5 py-1.5">
        {DESTINATIONS.map(({ id, path, shortLabelKey, icon: Icon }) => {
          const current = id === active;
          return (
            <li key={id} className="flex min-w-0 flex-1">
              {/* The router marks a link matching the location as the current
                  page by itself; exact matching leaves the mark to `active`. */}
              <Link
                to={path}
                activeOptions={{ exact: true }}
                aria-current={current ? "page" : undefined}
                className={current ? SLOT_CURRENT : SLOT_IDLE}
              >
                <Icon className="size-6" />
                <span className="max-w-full truncate text-2xs">{t(shortLabelKey)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
